import React, { useEffect, useRef, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { post } from "../api/api";
import moment from "moment/moment";
import mapboxgl from "mapbox-gl";
import { Card, Typography, Button, Divider, Box } from "@mui/joy";
import { FeatureCollection, Geometry, GeoJsonProperties } from "geojson";
import { getUser } from "../api/auth";
import ScopePremiumContent from "./ScopePremiumContent";
import { log } from "console";

// Style générique pour les cards
const cardClass = "bg-[#131537] p-4 rounded-lg shadow-lg";

interface FunctionResult {
    name: string;
    parameters: {
        start_date?: string;
        end_date?: string;
        id: string;
    };
    results: any[];
}

export const EventStatistics = ({ event }: { event: any }) => {
    const isPremium = false;
    const user = getUser();


    const [start] = useState(
        moment().startOf("year").subtract(1, "year").format("YYYY-MM-DDTHH:mm")
    );
    const [end] = useState(
        moment().startOf("year").add(1, "year").format("YYYY-MM-DDTHH:mm")
    );
    const [similarEventsCountByLocation, setSimilarEventsCountByLocation] =
        useState<any>(null);
    const [similarOrganizerCountByLocation, setSimilarOrganizerCountByLocation] =
        useState<any>(null);
    const [eventInteractionDeviation, setEventInteractionDeviation] =
        useState<any>(null);
    const [eventPotentialReach, setEventPotentialReach] = useState<any>(null);
    const [similarEventsHourlyDistribution, setSimilarEventsHourlyDistribution] =
        useState<any>(null);
    const [similarEventsHeatmapData, setSimilarEventsHeatmapData] =
        useState<any>(null);

    // Référence pour le conteneur Mapbox et l'instance de la carte
    const mapContainer = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);

    // Centre la carte sur l'événement grâce à ses coordonnées (latitude/longitude)
    const centerCoordinates: [number, number] =
        event.location_coordinates_gps_x && event.location_coordinates_gps_y
            ? [
                parseFloat(event.location_coordinates_gps_y),
                parseFloat(event.location_coordinates_gps_x),
            ]
            : [46.2276, 2.2137];

    const fetchData = async () => {
        try {
            const response = await post({
                url: "functions",
                data: {
                    functions: [
                        {
                            name: "getSimilarEventsCountByLocation",
                            parameters: {
                                start_date: start,
                                end_date: end,
                                id: event.id,
                            },
                        },
                        {
                            name: "getSimilarOrganizerCountByLocation",
                            parameters: {
                                start_date: start,
                                end_date: end,
                                id: event.id,
                            },
                        },
                        {
                            name: "getEventInteractionDeviation",
                            parameters: {
                                start_date: start,
                                end_date: end,
                                id: event.id,
                            },
                        },
                        {
                            name: "eventPotentialReachByUserPreferences",
                            parameters: { id: event.id },
                        },
                        {
                            name: "getSimilarEventsHourlyDistributionByCategory",
                            parameters: { id: event.id },
                        },
                        {
                            name: "getSimilarEventsHeatmapData",
                            parameters: { id: event.id },
                        },
                    ],
                },
            });
            if (response.status === 201) {
                processFunctionResults(response.data.functions);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const processFunctionResults = (functions: FunctionResult[]) => {
        const generateColor = (index: number, total: number): string => {
            const hue = Math.floor((360 / total) * index);
            return `hsl(${hue}, 70%, 50%, 0.6)`;
        };

        functions.forEach((func) => {
            if (func.name === "getSimilarEventsCountByLocation") {
                interface SubCategory {
                    category: string;
                    subCategory: string;
                    numberOfSimilarEvents: number;
                }
                interface LocationData {
                    localisation: string;
                    subCategoriesPerLocation: SubCategory[];
                }
                const results: LocationData[] = func.results;
                // Extraction dynamique des labels à partir des sous-catégories
                const subCategoriesSet = new Set<string>();
                results.forEach((location) => {
                    location.subCategoriesPerLocation.forEach((item) => {
                        subCategoriesSet.add(item.subCategory);
                    });
                });
                const orderedLabels = Array.from(subCategoriesSet);
                if(results.length > 0)
                {
                    setSimilarEventsCountByLocation({
                        labels: orderedLabels,
                        datasets: results.map((location, index) => {
                            const dataMap: Record<string, number> = {};
                            location.subCategoriesPerLocation.forEach((item) => {
                                dataMap[item.subCategory] = item.numberOfSimilarEvents;
                            });
                            const data = orderedLabels.map((label) => dataMap[label] || 0);
                            return {
                                label: location.localisation,
                                data: data,
                                backgroundColor: generateColor(index, results.length),
                            };
                        }),
                    });
                }
            } else if (func.name === "getSimilarOrganizerCountByLocation") {
                const results = func.results;
                if (results && results.length > 0) {
                    const orderedLabels = results.map((item: any) => item.localisation);
                    setSimilarOrganizerCountByLocation({
                        labels: orderedLabels,
                        datasets: [
                            {
                                label: "Nombre d'organisateurs similaires",
                                data: results.map(
                                    (item: any) => item.numberOfSimilarOrganizers
                                ),
                                backgroundColor: orderedLabels.map((_, index) =>
                                    generateColor(index, orderedLabels.length)
                                ),
                            },
                        ],
                    });
                } else {
                    setSimilarOrganizerCountByLocation(null);
                }
            } else if (func.name === "getEventInteractionDeviation") {
                const results = func.results;
                if (results && results.length > 0) {
                    setEventInteractionDeviation(results[0]);
                } else {
                    setEventInteractionDeviation(null);
                }
            } else if (func.name === "eventPotentialReachByUserPreferences") {
                const results = func.results;
                if (results && results.length > 0) {
                    const breakdownLabelsSet = new Set<string>();
                    results.forEach((organizer: any) => {
                        organizer.breakdown.forEach((item: any) => {
                            breakdownLabelsSet.add(item.subCategory);
                        });
                    });
                    const orderedBreakdownLabels = Array.from(breakdownLabelsSet);
                    setEventPotentialReach({
                        labels: orderedBreakdownLabels,
                        datasets: results.map((organizer: any, index: number) => {
                            const dataMap: Record<string, number> = {};
                            organizer.breakdown.forEach((item: any) => {
                                dataMap[item.subCategory] = item.potential;
                            });
                            const data = orderedBreakdownLabels.map(
                                (label) => dataMap[label] || 0
                            );
                            return {
                                label: organizer.organizerName ?? user?.name,
                                data: data,
                                backgroundColor: generateColor(index, results.length),
                            };
                        }),
                    });
                } else {
                    setEventPotentialReach(null);
                }
            } else if (func.name === "getSimilarEventsHourlyDistributionByCategory") {
                const results = func.results;
                if (results && results.length > 0) {
                    // Nous agrégeons les données horaires pour obtenir une distribution sur 24 heures.
                    const hourlyDistribution = new Array(24).fill(0);
                    results.forEach((category: any) => {
                        if (category.hourlyData && Array.isArray(category.hourlyData)) {
                            category.hourlyData.forEach((timeRange: any) => {
                                const start = timeRange.startHour;
                                const end = timeRange.endHour;
                                if (start <= end) {
                                    for (let h = start; h <= end; h++) {
                                        hourlyDistribution[h]++;
                                    }
                                } else {
                                    // L'événement traverse minuit
                                    for (let h = start; h < 24; h++) {
                                        hourlyDistribution[h]++;
                                    }
                                    for (let h = 0; h <= end; h++) {
                                        hourlyDistribution[h]++;
                                    }
                                }
                            });
                        }
                    });
                    setSimilarEventsHourlyDistribution({
                        labels: Array.from({ length: 24 }, (_, i) => i.toString()),
                        datasets: [
                            {
                                label: "Nombre d'événements actifs",
                                data: hourlyDistribution,
                                backgroundColor: "rgba(75, 192, 192, 0.5)",
                            },
                        ],
                    });
                } else {
                    setSimilarEventsHourlyDistribution(null);
                }
            } else if (func.name === "getSimilarEventsHeatmapData") {
                setSimilarEventsHeatmapData(func.results);
            }
        });
    };

    // Fonction pour recadrer la carte sur l'événement
    const handleRecenter = () => {
        if (mapRef.current) {
            mapRef.current.flyTo({
                center: centerCoordinates,
                zoom: 10,
                essential: true,
            });
        }
    };

    // Initialisation de Mapbox GL JS pour afficher la heatmap
    useEffect(() => {
        if (similarEventsHeatmapData && mapContainer.current) {
            mapboxgl.accessToken =
                "pk.eyJ1IjoiYXJuYXVkYmFnbmlzIiwiYSI6ImNqc25rcXJ1bjAyeTU0M3FoamF2cXdiMXgifQ.YVSW_BmKa3NngikAOKg1bA";
            const map = new mapboxgl.Map({
                container: mapContainer.current,
                style: "mapbox://styles/arnaudbagnis/cm7b5bgxt001j01s2f43nhrg7",
                center: centerCoordinates,
                zoom: 10,
            });
            mapRef.current = map;

            // Contrôles de navigation (zoom, rotation)
            map.addControl(new mapboxgl.NavigationControl(), "top-right");

            map.on("load", () => {
                const geojson: FeatureCollection<Geometry, GeoJsonProperties> = {
                    type: "FeatureCollection",
                    features: similarEventsHeatmapData.map((item: any) => ({
                        type: "Feature",
                        properties: {
                            weight: parseFloat(item.weight),
                        },
                        geometry: {
                            type: "Point",
                            // En GeoJSON, l'ordre est [longitude, latitude]
                            coordinates: [
                                parseFloat(item.gpsY),
                                parseFloat(item.gpsX),
                            ],
                        },
                    })),
                };

                if (map.getSource("similarEvents")) {
                    (map.getSource("similarEvents") as mapboxgl.GeoJSONSource).setData(geojson);
                } else {
                    map.addSource("similarEvents", {
                        type: "geojson",
                        data: geojson,
                    });
                    map.addLayer({
                        id: "similarEventsHeatmap",
                        type: "heatmap",
                        source: "similarEvents",
                        maxzoom: 15,
                        paint: {
                            "heatmap-weight": [
                                "interpolate",
                                ["linear"],
                                ["get", "weight"],
                                0,
                                0,
                                6,
                                1,
                            ],
                            "heatmap-intensity": [
                                "interpolate",
                                ["linear"],
                                ["zoom"],
                                0,
                                1,
                                15,
                                3,
                            ],
                            "heatmap-color": [
                                "interpolate",
                                ["linear"],
                                ["heatmap-density"],
                                0,
                                "rgba(33,102,172,0)",
                                0.2,
                                "rgb(103,169,207)",
                                0.4,
                                "rgb(209,229,240)",
                                0.6,
                                "rgb(253,219,199)",
                                0.8,
                                "rgb(239,138,98)",
                                1,
                                "rgb(178,24,43)",
                            ],
                            "heatmap-radius": [
                                "interpolate",
                                ["linear"],
                                ["zoom"],
                                0,
                                2,
                                15,
                                20,
                            ],
                            "heatmap-opacity": [
                                "interpolate",
                                ["linear"],
                                ["zoom"],
                                7,
                                1,
                                15,
                                0,
                            ],
                        },
                    });
                }
            });

            return () => map.remove();
        }
    }, [similarEventsHeatmapData, centerCoordinates, event]);

    useEffect(() => {
        console.log("similarEventsCountByLocation", similarEventsCountByLocation);
        // console.log("similarOrganizerCountByLocation", similarOrganizerCountByLocation);
        // console.log("eventInteractionDeviation", eventInteractionDeviation);
        // console.log("eventPotentialReach", eventPotentialReach);
        // console.log("similarEventsHourlyDistribution", similarEventsHourlyDistribution);
        // console.log("similarEventsHeatmapData", similarEventsHeatmapData);
    }, [
        similarEventsCountByLocation,
        similarOrganizerCountByLocation,
        eventInteractionDeviation,
        eventPotentialReach,
        similarEventsHourlyDistribution,
        similarEventsHeatmapData,
    ]);


    return (
        <div className="bg-[#0E102B] text-white min-h-screen p-6 event-stat-page">
            <div>
                <Typography level="h3" className="!font-bold" style={{ color: "white" }}>
                    📈 Performances de l'eevent
                </Typography>
                <Divider
                    orientation="horizontal"
                    className="!mb-5 !mt-1"
                    style={{ backgroundColor: "white", height: "1px" }}
                />

                <ScopePremiumContent scope="event_freemium_statistics">
                    <div className="grid grid-cols-4 gap-4 mb-6">
                        <Card className={cardClass}>
                            <Typography className="!text-3xl font-bold text-center">
                                {event.views}
                            </Typography>
                            <Typography className="!text-lg font-semibold text-center !-m-2">
                                👀 Vues
                            </Typography>
                        </Card>
                        <Card className={cardClass}>
                            <Typography className="!text-3xl font-bold text-center">
                                {event.likes}
                            </Typography>
                            <Typography className="!text-lg font-semibold text-center !-m-2">
                                ❤️ Likes
                            </Typography>
                        </Card>
                        <Card className={cardClass}>
                            <Typography className="!text-3xl font-bold text-center">
                                {event.shares}
                            </Typography>
                            <Typography className="!text-lg font-semibold text-center !-m-2">
                                🔁 Partages
                            </Typography>
                        </Card>
                        <Card className={cardClass}>
                            <Typography className="!text-3xl font-bold text-center">
                                {event.interests}
                            </Typography>
                            <Typography className="!text-lg font-semibold text-center !-m-2">
                                🎟 Clics Billetterie
                            </Typography>
                        </Card>
                    </div>
                </ScopePremiumContent>
            </div>

            <div>
                <Typography level="h3" className="!font-bold" style={{ color: "white" }}>
                    🏆 Votre impact vs le marché
                </Typography>
                <Divider
                    orientation="horizontal"
                    className="!mb-5 !mt-1"
                    style={{ backgroundColor: "white", height: "1px" }}
                />

                <div>
                    <ScopePremiumContent scope="event_freemium_statistics">
                        {eventInteractionDeviation ? (
                            <div className="grid grid-cols-3 gap-4 mb-6">
                                <Card className={cardClass}>
                                    <Typography className="!text-3xl font-bold text-center">
                                        {eventInteractionDeviation.targetViews}
                                    </Typography>
                                    <Typography className="!text-lg font-semibold text-center !-m-2">
                                        👀 Vues de l'évènement
                                    </Typography>
                                </Card>
                                <Card className={cardClass}>
                                    <Typography className="!text-3xl font-bold text-center">
                                        {eventInteractionDeviation.avgViews.toFixed(2)}
                                    </Typography>
                                    <Typography className="!text-lg font-semibold text-center !-m-2">
                                        🔝 Moyenne du marché
                                    </Typography>
                                </Card>
                                <Card className={cardClass}>
                                    <Typography className="!text-3xl font-bold text-center">
                                        {eventInteractionDeviation.diffViews.toFixed(2)}
                                    </Typography>
                                    <Typography className="!text-lg font-semibold text-center !-m-2">
                                        🔁 Différence
                                    </Typography>
                                </Card>
                            </div>
                        ) : (
                            <div className="p-4">
                                <Typography className="text-center !text-white">
                                    Pas assez de données pour afficher cette statistique.
                                </Typography>
                            </div>
                        )}
                    </ScopePremiumContent>
                </div>
            </div>

            <Typography level="h3" className="!font-bold" style={{ color: "white" }}>
                📊 Statistiques Premium
            </Typography>
            <Divider
                orientation="horizontal"
                className="!mb-5 !mt-1"
                style={{ backgroundColor: "white", height: "1px" }}
            />

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-6">
                <Card className={cardClass}>
                    <Typography level="h3" className="text-lg font-semibold mb-2">
                        🔎 Eevents similaires
                    </Typography>
                    <ScopePremiumContent scope="event_premium_statistics">
                        {similarEventsCountByLocation ? (
                            <Bar data={similarEventsCountByLocation} />
                        ) : (
                            <div className="p-4">
                                <Typography className="text-center !text-black">
                                    Pas assez de données pour afficher cette statistique.
                                </Typography>
                            </div>
                        )}
                    </ScopePremiumContent>
                </Card>

                <Card className={cardClass}>
                    <Typography level="h3" className="text-lg font-semibold mb-2">
                        👥 Organisateurs similaires
                    </Typography>
                    <ScopePremiumContent scope="event_premium_statistics">
                        {similarOrganizerCountByLocation ? (
                            <Bar data={similarOrganizerCountByLocation} />
                        ) : (
                            <div className="p-4">
                                <Typography className="text-center !text-black">
                                    Pas assez de données pour afficher cette statistique.
                                </Typography>
                            </div>
                        )}
                    </ScopePremiumContent>
                </Card>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-6 !mt-5 !mb-5">
                <Card className={cardClass}>
                    <Typography level="h3" className="text-lg font-semibold mb-2">
                        🚀 Audience potentielle
                    </Typography>
                    <ScopePremiumContent scope="event_premium_statistics">
                        {eventPotentialReach ? (
                            <Bar data={eventPotentialReach} />
                        ) : (
                            <div className="p-4">
                                <Typography className="text-center !text-black">
                                    Pas assez de données pour afficher cette statistique.
                                </Typography>
                            </div>
                        )}
                    </ScopePremiumContent>
                </Card>

                <Card className={cardClass}>
                    <Typography level="h3" className="text-lg font-semibold mb-2">
                        ⏰ Tendances horaires
                    </Typography>
                    <ScopePremiumContent scope="event_premium_statistics">
                        {similarEventsHourlyDistribution ? (
                            // Utilisation d'un graphique en barres pour afficher le nombre d'événements actifs par heure
                            <Bar data={similarEventsHourlyDistribution} />
                        ) : (
                            <div className="p-4">
                                <Typography className="text-center !text-black">
                                    Pas assez de données pour afficher cette statistique.
                                </Typography>
                            </div>
                        )}
                    </ScopePremiumContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1">
                <Card className={cardClass}>
                    <Typography level="h3" className="text-lg font-semibold mb-2">
                        📍 Heatmap des Eevents
                    </Typography>
                    <ScopePremiumContent scope="event_premium_statistics">
                        <div ref={mapContainer} style={{ width: "100%", height: "400px" }} />
                        <Button onClick={handleRecenter} sx={{ mb: 2 }} variant="solid" color="primary">
                            Recentrer sur l'évènement
                        </Button>
                    </ScopePremiumContent>
                </Card>
            </div>
        </div>
    );
};

export default EventStatistics;